import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../ui/table';
import { Avatar } from '../../ui/avatar';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  Edit, 
  Trash2, 
  Copy, 
  Calendar,
  Mail,
  User
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const UserTable = ({ 
  users, 
  sortConfig, 
  onSort, 
  onEdit, 
  onDelete, 
  onClone,
  loading = false 
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const getSubscriptionBadge = (user) => {
    const subscription = user.user_subscriptions?.[0];
    if (!subscription) {
      return <Badge variant="secondary">No Subscription</Badge>;
    }

    const tierName = subscription.subscription_tiers?.name || 'Unknown';
    const status = subscription.status || 'unknown';
    
    const variant = {
      'active': tierName === 'pro' ? 'default' : 'secondary',
      'canceled': 'destructive',
      'past_due': 'destructive',
      'trialing': 'outline'
    }[status] || 'secondary';

    return (
      <Badge variant={variant}>
        {subscription.subscription_tiers?.display_name || tierName} ({status})
      </Badge>
    );
  };

  const ActionButton = ({ onClick, icon: Icon, label, variant = "ghost" }) => (
    <Button
      variant={variant}
      size="sm"
      onClick={onClick}
      className="h-8 w-8 p-0"
      title={label}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-500 dark:text-gray-400">
          Loading users...
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <User className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">No users found</p>
        <p className="text-sm">Try adjusting your search or add a new user</p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50 dark:bg-gray-800">
            <TableHead>User</TableHead>
            <TableHead 
              sortable 
              sortKey="email" 
              sortConfig={sortConfig} 
              onSort={onSort}
            >
              Email
            </TableHead>
            <TableHead 
              sortable 
              sortKey="subscription_tier" 
              sortConfig={sortConfig} 
              onSort={onSort}
            >
              Subscription
            </TableHead>
            <TableHead 
              sortable 
              sortKey="created_at" 
              sortConfig={sortConfig} 
              onSort={onSort}
            >
              Created
            </TableHead>
            <TableHead 
              sortable 
              sortKey="last_sign_in_at" 
              sortConfig={sortConfig} 
              onSort={onSort}
            >
              Last Sign In
            </TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.user_id}>
              <TableCell>
                <div className="flex items-center space-x-3">
                  <Avatar
                    size="sm"
                    initials={user.avatar_initials}
                    color={user.avatar_color}
                    type={user.avatar_type}
                    src={user.avatar_url}
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      @{user.username}
                    </div>
                  </div>
                </div>
              </TableCell>
              
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-mono">
                    {user.email}
                  </span>
                  {user.email_confirmed_at && (
                    <Badge variant="outline" className="text-xs">
                      Verified
                    </Badge>
                  )}
                </div>
              </TableCell>
              
              <TableCell>
                {getSubscriptionBadge(user)}
              </TableCell>
              
              <TableCell>
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(user.created_at)}</span>
                </div>
              </TableCell>
              
              <TableCell>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formatDate(user.last_sign_in_at)}
                </span>
              </TableCell>
              
              <TableCell>
                <div className="flex items-center space-x-1">
                  <ActionButton
                    onClick={() => onEdit(user)}
                    icon={Edit}
                    label="Edit user"
                  />
                  <ActionButton
                    onClick={() => onClone(user)}
                    icon={Copy}
                    label="Clone user"
                  />
                  <ActionButton
                    onClick={() => onDelete(user)}
                    icon={Trash2}
                    label="Delete user"
                    variant="ghost"
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserTable;
